import { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { SecretCard } from "@/components/webhooks/SecretCard";
import { CodeSamples } from "@/components/webhooks/CodeSamples";
import { SendTest, SignatureChecker } from "@/components/webhooks/SigningTools";

const RULES = [
    "Every webhook carries X-PayGateway-Signature = hex(HMAC-SHA256(secret, raw request body)).",
    "Compute the HMAC over the exact raw bytes received — never over re-encoded or pretty-printed JSON.",
    "Compare with a constant-time function (timingSafeEqual / hmac.compare_digest / hash_equals / MessageDigest.isEqual).",
    "Reject with HTTP 401 if the header is missing or does not match. Never credit a customer from an unverified webhook.",
    "Return 2xx quickly. Deliveries can repeat — de-duplicate on order_id / payout_id + status.",
    "PayIn callbacks and Payout webhooks use separate secrets. Store them in environment variables, never in code.",
];

export default function PanelWebhookSigning() {
    const { user } = useAuth();
    const canEdit = user?.role === "partner_admin";
    const [info, setInfo] = useState(null);
    const load = () => api.get("/partner/webhook-signing").then((r) => setInfo(r.data)).catch(() => setInfo({}));
    useEffect(() => { load(); }, []);

    return (
        <div className="space-y-6 max-w-5xl" data-testid="panel-webhook-signing">
            <div>
                <h1 className="text-2xl font-bold flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-indigo-600" /> Webhook Signing Guide</h1>
                <p className="text-sm text-slate-500 mt-1">Verify that every PayIn and Payout webhook really came from us before you act on it.</p>
            </div>
            <ol className="bg-indigo-50/60 border border-indigo-100 rounded-lg p-4 space-y-1.5 text-xs text-slate-700 list-decimal list-inside" data-testid="wh-sign-rules">
                {RULES.map((r) => <li key={r}>{r}</li>)}
            </ol>
            <div className="grid md:grid-cols-2 gap-4">
                <SecretCard kind="payin" info={info?.payin} canEdit={canEdit} onChanged={load} />
                <SecretCard kind="payout" info={info?.payout} canEdit={canEdit} onChanged={load} />
            </div>
            <div>
                <h2 className="text-base md:text-lg font-semibold mb-2">Sample verification code</h2>
                <CodeSamples />
            </div>
            <div>
                <h2 className="text-base md:text-lg font-semibold mb-2">Test my verification</h2>
                {canEdit ? (
                    <div className="grid lg:grid-cols-2 gap-4"><SendTest /><SignatureChecker /></div>
                ) : (
                    <div className="text-xs text-slate-500" data-testid="wh-sign-readonly">Ask your partner admin to run the test tools.</div>
                )}
            </div>
        </div>
    );
}
